import type { ReactNode } from 'react';

// ------------------------------------------------------------------
// Layout composition primitives — story 2-147sa (P10 §2.6/§2.7 row 6, DOD).
// Built over `2-147o`'s canonical contract, immediately after `2-147r`'s six
// governed action hosts (`ActionHosts.tsx`) — same "one file, one closed
// family of related primitives" precedent, applied here to layout instead
// of actions.
// ------------------------------------------------------------------
//
// ── THE GAP ─────────────────────────────────────────────────────────────
// P10 §2.7 row 6 ("Grids") already named this as a real, unbuilt gap: "No
// `Grid` primitive exists in `components/ui` — every card/KPI grid reaches
// for `grid gap-4 md:grid-cols-N` directly today." The same is true, unnamed
// until now, of every other shape below — `DashboardPage.tsx`'s `space-y-6`
// stack, `PageHeader.tsx`'s `flex flex-wrap items-center gap-2` action row,
// `Breadcrumbs.tsx`'s `flex items-center gap-1` no-wrap crumb, `OrgAccessPage
// .tsx`'s two-pane `grid-cols-[1fr_20rem]` grid — each hand-rolled locally,
// nothing named. This file is the closed set every layout/page/composite in
// this chain builds from from this point forward — a page author reaches for
// `Stack`/`Grid`/`Split`/etc. instead of re-deriving a `flex`/`grid`
// className. `2-147sb` (next) builds the six named page layouts directly on
// top of these; `2-147xb` (after that) starts rejecting new bespoke
// `flex`/`grid` layout decisions made outside this file.
//
// ── SEVEN PRIMITIVES, NOT EIGHT — DIVIDER ALREADY RESOLVED ────────────────
// The story names `Divider` as an eighth primitive, but P10 §2.7 row 1
// already settled this responsibility, TWICE: `2-147o`'s own canonical
// matrix ("Dividers | `border-border` (default hairline) / `border-border-
// strong` (firm separator) applied directly, or `divide-y` for stacked rows
// | ... applied directly") and `2-147qb`'s independent re-investigation
// ("`Heading`/`Text`/`Meta`/`Label`/`Divider` do **not** exist as components
// and ... are NOT supposed to — that tie is already broken in the contract
// itself. Building wrapper components for them would have reopened a closed
// tie, not converged onto it" — `DesignSystemPage.tsx`'s own
// `TypographyStatePrimitivesProofDemo` comment, verbatim). `2-147qb` also
// independently measured the divider pattern and found zero drift: `border-
// border`/`border-border-strong`/`divide-y` already the only pattern in use
// tree-wide. Building a `Divider` component here would re-open a tie this
// lane already closed twice — the same trap `2-147qb` itself named and
// avoided for `Heading`/`Text`/`Meta`/`Label`. So this file ships the seven
// primitives that are genuinely unbuilt gaps (`Container`, `Stack`,
// `Inline`, `Cluster`, `Grid`, `Split`, `Section`) and leaves Divider as the
// already-resolved "apply the utility directly" answer, gallery-demonstrated
// already at `DesignSystemPage.tsx`'s "Dividers — border-border / border-
// border-strong / divide-y, applied directly (P10 §2.7 row 1)" example
// inside the Typography/state primitives Section (`2-147qb`) — cited below,
// not re-demonstrated as a second copy.
//
// ── GAP SCALE ───────────────────────────────────────────────────────────
// Every gap/space value below is Tailwind's own spacing scale
// (`gap-*`/`space-y-*`), the SAME scale every real consumer already reaches
// for (`PageHeader.tsx` gap-2/gap-4, `DashboardPage.tsx` space-y-6/space-y-4,
// `OrgAccessPage.tsx` gap-6, `ReviewDetailPage.tsx` gap-4,
// `ReportDetailPage.tsx` gap-5) — not a new number invented here. `LayoutGap`
// below is a closed subset of that scale (the values real call sites already
// use), spent only through the static `GAP_CLASS`/`SPACE_Y_CLASS` lookup
// maps — never `` `gap-${n}` `` string interpolation, which Tailwind's static
// class-name scanner cannot see and would silently fail to generate CSS for
// (same "full literal string, not a template" discipline
// `components/gallery/VariantGrid.tsx`'s `COLUMNS_CLASS` already
// establishes).
// `0.5` added by story 2-147xd — `SectionNav.tsx`'s own label/count caption
// stack real call site (`gap-0.5`, its pre-existing value, unchanged),
// closing a design-language/layout-only-through-primitives finding; the
// same closed-subset-of-Tailwind's-real-scale discipline this type's own
// header comment already states, just one more real value real call sites
// already used.
export type LayoutGap = 0 | 0.5 | 1 | 1.5 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

const GAP_CLASS: Record<LayoutGap, string> = {
  0: 'gap-0',
  0.5: 'gap-0.5',
  1: 'gap-1',
  1.5: 'gap-1.5',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
};

// Per-axis gap classes — story 2-147xd, `CaptureHeader.tsx`/`OrgFeaturesPage
// .tsx`'s own real header rows (`gap-x-3 gap-y-2` / `gap-x-3 gap-y-1`): same
// `gap-x`, different `gap-y`, so `Cluster`'s single `gap` prop can't express
// either without forcing a mismatched pixel value onto one site. `gapX`/
// `gapY` below OVERRIDE `gap` per-axis when supplied — same closed
// `LayoutGap` scale, same static-lookup discipline, just two maps instead
// of one.
const GAP_X_CLASS: Record<LayoutGap, string> = {
  0: 'gap-x-0',
  0.5: 'gap-x-0.5',
  1: 'gap-x-1',
  1.5: 'gap-x-1.5',
  2: 'gap-x-2',
  3: 'gap-x-3',
  4: 'gap-x-4',
  5: 'gap-x-5',
  6: 'gap-x-6',
  8: 'gap-x-8',
  10: 'gap-x-10',
  12: 'gap-x-12',
};

const GAP_Y_CLASS: Record<LayoutGap, string> = {
  0: 'gap-y-0',
  0.5: 'gap-y-0.5',
  1: 'gap-y-1',
  1.5: 'gap-y-1.5',
  2: 'gap-y-2',
  3: 'gap-y-3',
  4: 'gap-y-4',
  5: 'gap-y-5',
  6: 'gap-y-6',
  8: 'gap-y-8',
  10: 'gap-y-10',
  12: 'gap-y-12',
};

const SPACE_Y_CLASS: Record<LayoutGap, string> = {
  0: 'space-y-0',
  0.5: 'space-y-0.5',
  1: 'space-y-1',
  1.5: 'space-y-1.5',
  2: 'space-y-2',
  3: 'space-y-3',
  4: 'space-y-4',
  5: 'space-y-5',
  6: 'space-y-6',
  8: 'space-y-8',
  10: 'space-y-10',
  12: 'space-y-12',
};

type CrossAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
const ALIGN_CLASS: Record<CrossAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

type MainJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
const JUSTIFY_CLASS: Record<MainJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

type Tag = keyof JSX.IntrinsicElements;

// ------------------------------------------------------------------
// Container — max-width + centering. Wraps `2-131`'s own three named
// content-width modes (`tokens.css`, story 2-131 §1.1) verbatim — the SAME
// three keys/values `DesignSystemPage.tsx`'s "Shell content-width modes"
// Section (story 2-131) already demonstrates via
// `style={{ maxWidth: 'var(--content-reading)' }}` — this component is that
// exact call-site pattern, named and reusable instead of hand-copied. Reads
// the token through `style`, never restates its value — the same "a
// component READING a token is not a violation" carve-out
// `check-shell-geometry.mjs`'s own header comment states; verified clean by
// running that script after this file was added (see this story's own
// session record).
// ------------------------------------------------------------------
export type ContainerWidth = 'reading' | 'operational' | 'fluid';

// Exported (additive, story 2-147sb) so a layout composing a primitive that
// takes a raw CSS max-width string — `AppFrame.tsx`'s own `contentMaxWidth`
// prop, predating `Container` — can resolve one of `2-131`'s three named
// modes through the SAME lookup `Container` itself uses, rather than
// restating a token's value a second time (`PageLayouts.tsx`'s
// `WorkspaceLayout`, the one real consumer today).
export const CONTAINER_WIDTH_VAR: Record<ContainerWidth, string> = {
  reading: 'var(--content-reading)',
  operational: 'var(--content-operational)',
  fluid: 'var(--content-fluid)',
};

export interface ContainerProps {
  /** Which of `2-131`'s three content-width modes to clamp at. Defaults to `'operational'` — the same value `AppShell`'s own top-level canvas already renders every shell-routed page at (P10 §2.6). */
  width?: ContainerWidth;
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  children: ReactNode;
}

export function Container({ width = 'operational', as = 'div', className = '', 'data-testid': testId, children }: ContainerProps): JSX.Element {
  const Component = as;
  return (
    <Component className={`mx-auto w-full ${className}`} style={{ maxWidth: CONTAINER_WIDTH_VAR[width] }} data-testid={testId}>
      {children}
    </Component>
  );
}

// ------------------------------------------------------------------
// TokenBox — story 2-147xd. Generalizes `Container`'s own established
// pattern ("a primitive READING a `var(--token)`/caller-supplied CSS value
// into a real inline `style` prop is compliant — check-shell-geometry.mjs's
// own header comment already carves this out; only a component STATING an
// arbitrary literal itself is debt") from `maxWidth` alone to the small
// closed set of geometry properties three layout-primitive shell files
// (`AppFrame.tsx`'s rail/panel slot widths and canvas max-width;
// `AppShell.tsx`'s decorative glow-blob inset+size; `Sidebar.tsx`'s
// `top: var(--shell-header-h)` fixed-position offset) each independently
// needed the exact same mechanism for — P10 §4.4's own table already names
// these as REAL debt (not an exempt layer), because layout-primitive is
// deliberately not in `INLINE_STYLE_STRUCTURAL_LAYERS` the way primitive/
// specialist-renderer/gallery are. Every value passed through here is
// EITHER a `var(--token)` reference or a value already threaded down from
// a caller prop — never a literal number invented at the call site inside
// one of those three files, the same discipline `Container` itself already
// holds to.
export interface TokenBoxProps {
  width?: string;
  height?: string;
  maxWidth?: string;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  as?: Tag;
  className?: string;
  'aria-hidden'?: boolean;
  'data-testid'?: string;
  children?: ReactNode;
  // Generic data-* passthrough — AppFrame's own `data-appframe-slot="rail"
  // | "panel"` marker is the one real consumer beyond `data-testid` today.
  [dataAttr: `data-${string}`]: unknown;
}

export function TokenBox({
  width,
  height,
  maxWidth,
  top,
  right,
  bottom,
  left,
  as = 'div',
  className = '',
  'aria-hidden': ariaHidden,
  'data-testid': testId,
  children,
  ...dataAttrs
}: TokenBoxProps): JSX.Element {
  const Component = as;
  return (
    <Component
      className={className}
      style={{ width, height, maxWidth, top, right, bottom, left }}
      aria-hidden={ariaHidden}
      data-testid={testId}
      {...dataAttrs}
    >
      {children}
    </Component>
  );
}

// ------------------------------------------------------------------
// Stack — vertical flex + gap. Formalizes the `space-y-*` vertical rhythm
// every page in the tree already reaches for by hand (`DashboardPage.tsx`'s
// top-level `space-y-6`, its own card interiors' `space-y-4`;
// `PlatformConsolePage.tsx`'s section `space-y-3`) — one named primitive
// instead of re-typing the class per call site. Renders `space-y-*`
// (margin-based), not `flex flex-col gap-*` — byte-identical visual result
// for a non-wrapping vertical list, and matches the exact utility every real
// consumer above already uses, so a future Stack-migration story (`2-147w*`)
// changes nothing about the resulting DOM/CSS for those call sites.
// ------------------------------------------------------------------
export interface StackProps {
  /** Vertical rhythm between children. Defaults to `4` (1rem) — `DashboardPage.tsx`'s own card-interior rhythm. */
  gap?: LayoutGap;
  align?: Exclude<CrossAlign, 'baseline'>;
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  /** Optional ARIA role passthrough — mirrors `Cluster`'s, e.g. a `Stack as="nav"` that also needs `role="navigation"` made explicit. */
  role?: string;
  /** Optional accessible name passthrough — mirrors `Cluster`'s. Real need: `DesignSystemPage.tsx`'s `AppFrame` rail demo (`Stack as="nav" aria-label="…"`, story 2-147xd) — without this the prop is TS-excess-property-check-exempt (hyphenated JSX attribute names bypass that check) so it silently vanished at runtime instead of erroring, stripping the `<nav>`'s accessible name. */
  'aria-label'?: string;
  children: ReactNode;
}

export function Stack({
  gap = 4,
  align,
  as = 'div',
  className = '',
  'data-testid': testId,
  role,
  'aria-label': ariaLabel,
  children,
}: StackProps): JSX.Element {
  const Component = as;
  const alignClass = align ? `flex flex-col ${ALIGN_CLASS[align]}` : '';
  return (
    <Component
      className={`${alignClass ? alignClass : ''} ${SPACE_Y_CLASS[gap]} ${className}`.trim()}
      data-testid={testId}
      role={role}
      aria-label={ariaLabel}
    >
      {children}
    </Component>
  );
}

// ------------------------------------------------------------------
// Inline vs Cluster — the real distinction, grounded in live usage, not
// guessed. Grepped every `flex items-center gap-*` / `flex flex-wrap
// items-center gap-*` call site tree-wide before choosing the split:
//
//   INLINE = a single unbreakable horizontal unit that must never wrap
//   mid-item — an icon beside its own label, a value beside its own status
//   dot. Real precedent: `Breadcrumbs.tsx`'s per-crumb `<li
//   className="flex items-center gap-1">` (the chevron+label pair inside ONE
//   crumb — the OUTER `<ol>` around the crumb list itself DOES wrap, that's
//   Cluster's job, see below); `SaveStateIndicator.tsx`'s `inline-flex
//   items-center gap-1.5` (icon+status text); `StatusChip.tsx`'s
//   `inline-flex items-center gap-1.5` (dot+label). No `flex-wrap` in any of
//   these — wrapping mid-pair would break the pair's own meaning (a
//   dangling chevron, an orphaned status dot).
//
//   CLUSTER = a group of independent, wrap-capable items that reflow onto a
//   new line as a group once the row runs out of room — each item stays
//   whole, but the GROUP breaks. Real precedent: `PageHeader.tsx`'s chips
//   row (`flex flex-wrap items-center gap-1.5`), actions row (`flex
//   flex-wrap items-center gap-2`) and toolbar row (`flex flex-wrap
//   items-center gap-2`); `BulkActionBar.tsx`'s own action row (`flex
//   flex-wrap items-center gap-2`); `Callout.tsx`'s actions slot (`flex
//   flex-wrap items-center gap-2`); `PhotoInput.tsx`'s thumbnail grid (`flex
//   flex-wrap gap-2`). This is also the shape a tag/chip group needs (P10
//   §2.7 row 4's `Badge`/`OptionChip` groups) even though no real multi-chip
//   group call site was found hand-rolling its own wrapper today — the
//   PageHeader chips row already IS one.
//
// So: Inline never wraps (a single flex item-pair's own internal layout);
// Cluster always can (a row of independent items, PageHeader's own three
// rows being the clearest live precedent). Same gap scale, opposite
// `flex-wrap` default — the one axis that actually differs between them in
// every real call site found.
// ------------------------------------------------------------------
export interface InlineProps {
  /** Defaults to `2` (0.5rem) — `Breadcrumbs.tsx`'s/`SaveStateIndicator.tsx`'s own icon+label gap is 1/1.5; 2 is the more common tight-pair gap elsewhere (e.g. `CopyField.tsx`). Callers with a tighter pair pass `gap={1}` or `gap={1.5}`. */
  gap?: LayoutGap;
  /** Overrides `gap` on the horizontal axis only, story 2-147xd — see `gapX`/`gapY`'s shared doc comment above `GAP_X_CLASS`. */
  gapX?: LayoutGap;
  /** Overrides `gap` on the vertical axis only, story 2-147xd. */
  gapY?: LayoutGap;
  align?: CrossAlign;
  /** Renders `inline-flex` instead of `flex` — for a pair embedded inside running text/inline content (`SaveStateIndicator.tsx`'s own real shape). Defaults to `false` (block-level `flex`, `Breadcrumbs.tsx`'s per-crumb shape). */
  inline?: boolean;
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  /** Raw HTML `title` (tooltip) passthrough — story 2-147xd, `OrgFeaturesPage.tsx`'s platform-managed badge (`title="Set by the platform owner…"` on the icon+label pair itself, not a wrapping element). */
  title?: string;
  children: ReactNode;
}

export function Inline({
  gap = 2,
  gapX,
  gapY,
  align = 'center',
  inline = false,
  as = 'div',
  className = '',
  'data-testid': testId,
  title,
  children,
}: InlineProps): JSX.Element {
  const Component = as;
  const gapClass = gapX != null || gapY != null ? `${GAP_X_CLASS[gapX ?? gap]} ${GAP_Y_CLASS[gapY ?? gap]}` : GAP_CLASS[gap];
  return (
    <Component
      className={`${inline ? 'inline-flex' : 'flex'} flex-nowrap ${ALIGN_CLASS[align]} ${gapClass} ${className}`}
      data-testid={testId}
      title={title}
    >
      {children}
    </Component>
  );
}

export interface ClusterProps {
  /** Defaults to `2` (0.5rem) — `PageHeader.tsx`'s own actions/toolbar row gap. */
  gap?: LayoutGap;
  /** Overrides `gap` on the horizontal axis only, story 2-147xd — see `gapX`/`gapY`'s shared doc comment above `GAP_X_CLASS`. Real need: `CaptureHeader.tsx`'s/`OrgFeaturesPage.tsx`'s own header rows (`gap-x-3 gap-y-2` / `gap-x-3 gap-y-1` — same `gap-x`, different `gap-y`). */
  gapX?: LayoutGap;
  /** Overrides `gap` on the vertical axis only, story 2-147xd. */
  gapY?: LayoutGap;
  align?: Exclude<CrossAlign, 'baseline'>;
  justify?: MainJustify;
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  /** Raw HTML `title` (tooltip) passthrough, story 2-147xd — mirrors `Inline`'s. */
  title?: string;
  /** Optional ARIA role passthrough — e.g. `OAuthProviderButtons.tsx`'s "or" divider row (`role="separator"`, story 2-147wg), a real second consumer beyond the plain grouping role every other call site needs. */
  role?: string;
  /** Optional accessible name passthrough, paired with `role` above. */
  'aria-label'?: string;
  /**
   * Stacks vertically below `sm`, becomes a horizontal `items-center
   * justify-between` row at `sm` and up — the "header row that stacks on
   * mobile" recipe, grounded in three identical real call sites
   * (`PublicFooter.tsx`, `SettingsSection.tsx`, `BillingPage.tsx`'s own
   * summary row, all `flex flex-col gap-* sm:flex-row sm:items-center
   * sm:justify-between` byte-for-byte, story 2-147xd). When set, this
   * REPLACES `align`/`justify`'s effect entirely (both fire only at `sm`+,
   * matching every real consumer's own "no cross-align below sm" need) —
   * not composed with them, since Tailwind's JIT scanner needs the full
   * `sm:items-center`/`sm:justify-between` class strings literal in source,
   * not assembled from the unprefixed `ALIGN_CLASS`/`JUSTIFY_CLASS` maps.
   */
  stackBelowSm?: boolean;
  children: ReactNode;
}

export function Cluster({
  gap = 2,
  gapX,
  gapY,
  align = 'center',
  justify = 'start',
  as = 'div',
  className = '',
  'data-testid': testId,
  title,
  role,
  'aria-label': ariaLabel,
  stackBelowSm = false,
  children,
}: ClusterProps): JSX.Element {
  const Component = as;
  const layoutClasses = stackBelowSm
    ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between'
    : `flex flex-wrap ${ALIGN_CLASS[align]} ${JUSTIFY_CLASS[justify]}`;
  const gapClass = gapX != null || gapY != null ? `${GAP_X_CLASS[gapX ?? gap]} ${GAP_Y_CLASS[gapY ?? gap]}` : GAP_CLASS[gap];
  return (
    <Component
      className={`${layoutClasses} ${gapClass} ${className}`}
      data-testid={testId}
      title={title}
      role={role}
      aria-label={ariaLabel}
    >
      {children}
    </Component>
  );
}

// ------------------------------------------------------------------
// Grid — CSS grid + gap + responsive columns. Closes P10 §2.7 row 6's own
// named gap verbatim ("No `Grid` primitive exists ... every card/KPI grid
// reaches for `grid gap-4 md:grid-cols-N` directly today"). Real precedent
// for the responsive-column shape: `DashboardPage.tsx`'s KPI strip (`grid
// gap-4 md:grid-cols-4`), `SitesPage.tsx`'s card grid (`grid gap-4
// sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`), `SiteDetailPage.tsx`'s
// field grid (`grid gap-4 sm:grid-cols-2`). `columns` takes one column count
// per breakpoint (all optional — omit a breakpoint to inherit the previous
// one, exactly how the real call sites above already read); every value
// spent through a static per-breakpoint lookup map, same
// Tailwind-static-scan discipline as the gap scale above.
// ------------------------------------------------------------------
type GridColumnCount = 1 | 2 | 3 | 4 | 5 | 6;

export interface GridColumns {
  base?: GridColumnCount;
  sm?: GridColumnCount;
  md?: GridColumnCount;
  lg?: GridColumnCount;
  xl?: GridColumnCount;
}

const BASE_COLS_CLASS: Record<GridColumnCount, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};
const SM_COLS_CLASS: Record<GridColumnCount, string> = {
  1: 'sm:grid-cols-1',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-4',
  5: 'sm:grid-cols-5',
  6: 'sm:grid-cols-6',
};
const MD_COLS_CLASS: Record<GridColumnCount, string> = {
  1: 'md:grid-cols-1',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
  5: 'md:grid-cols-5',
  6: 'md:grid-cols-6',
};
const LG_COLS_CLASS: Record<GridColumnCount, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};
const XL_COLS_CLASS: Record<GridColumnCount, string> = {
  1: 'xl:grid-cols-1',
  2: 'xl:grid-cols-2',
  3: 'xl:grid-cols-3',
  4: 'xl:grid-cols-4',
  5: 'xl:grid-cols-5',
  6: 'xl:grid-cols-6',
};

export interface GridProps {
  /** Column count per breakpoint. Defaults to `{ base: 1 }` — single column below every breakpoint, matching every real consumer's own mobile fallback. */
  columns?: GridColumns;
  /** Defaults to `4` (1rem) — `DashboardPage.tsx`'s/`SitesPage.tsx`'s own KPI/card grid gap. */
  gap?: LayoutGap;
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  /** Optional ARIA role passthrough — mirrors `Cluster`'s/`Stack`'s. Real need: `OrgAccessPage.tsx`'s plan-picker grid (`role="group"`, story 2-147xd). */
  role?: string;
  /** Optional accessible name passthrough, paired with `role` above — mirrors `Cluster`'s/`Stack`'s. */
  'aria-label'?: string;
  children: ReactNode;
}

export function Grid({
  columns = { base: 1 },
  gap = 4,
  as = 'div',
  className = '',
  'data-testid': testId,
  role,
  'aria-label': ariaLabel,
  children,
}: GridProps): JSX.Element {
  const Component = as;
  const classes = [
    'grid',
    GAP_CLASS[gap],
    columns.base ? BASE_COLS_CLASS[columns.base] : undefined,
    columns.sm ? SM_COLS_CLASS[columns.sm] : undefined,
    columns.md ? MD_COLS_CLASS[columns.md] : undefined,
    columns.lg ? LG_COLS_CLASS[columns.lg] : undefined,
    columns.xl ? XL_COLS_CLASS[columns.xl] : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Component className={classes} data-testid={testId} role={role} aria-label={ariaLabel}>
      {children}
    </Component>
  );
}

// ------------------------------------------------------------------
// Split — two-pane layout with a named ratio, collapsing to one stacked
// column below `lg`. Grounded in three real two-pane grids: `ReportDetailPage
// .tsx`'s rail split (`grid gap-5 lg:grid-cols-[1fr,320px]`),
// `ReviewDetailPage.tsx`'s desktop compare (`hidden gap-4 lg:grid
// lg:grid-cols-2`), `OrgAccessPage.tsx`'s two paired form/summary grids
// (`grid gap-6 lg:grid-cols-[1fr_20rem]` / `grid gap-6
// lg:grid-cols-[18rem_1fr]`) — all `lg:`-gated, all two panes, all currently
// hand-rolled per page.
//
// `ReviewDetailPage.tsx`'s own header comment (story 2-141, predates this
// story) argued AGAINST promoting its `lg:grid-cols-2` to a `SplitView`
// primitive: "a primitive whose entire body is one Tailwind class ... If a
// resizable divider or a persisted split ratio ever lands, THAT is a
// primitive; a two-column layout is not." This story's `Split` is
// deliberately not that one-class wrapper — it bundles a closed set of named
// ratios (below) PLUS the responsive collapse contract (stack under `lg`,
// two-pane at `lg:` and above) every real two-pane consumer above already
// hand-derives independently, as ONE reusable decision instead of three. Not
// migrating `ReviewDetailPage.tsx`/`ReportDetailPage.tsx`/`OrgAccessPage.tsx`
// onto it here — that's `2-147sb`/`2-147w*`'s job, this story ships the
// primitive only (same "build it, demonstrate it, don't migrate consumers
// yet" scoping `2-147r`'s own action hosts already established).
//
// `--panel-w` — story 2-147xd mints this token, closing the gap the
// paragraph above (pre-2-147xd) deliberately left open "without a second
// real consumer to justify it": `AppFrame.tsx`'s own `panelWidth` prop
// already defaulted to a literal `'20rem'`, and `ReportDetailPage.tsx`'s
// `lg:grid-cols-[1fr,320px]` fixed secondary pane is the SAME 320px/20rem
// value, same "fixed-width side pane" shape — two real, independent
// consumers, not one. The token now lives in `tokens.css` (`--panel-w:
// 20rem`, alongside `--panel-gap`); `AppFrame.tsx`'s `panelWidth` default
// reads it via `style` (`TokenBox`'s own established pattern) instead of
// restating the literal a second time, and `Split`'s `ratio="panel"` (below)
// gives `ReportDetailPage.tsx` the same value through a real named
// `tailwind.config.ts` utility (`grid-cols-split-panel: '1fr var(--panel-w)'`
// — a STATIC class whose VALUE happens to read a token, the same
// `Container`/`TokenBox` carve-out this file's own header comment already
// states, never a `grid-cols-[...]` arbitrary-value bracket in source).
//
// Marketing hero asymmetric ratios (story 2-147xd) — `LandingPage.tsx`/
// `SecurityPage.tsx`/`FeaturesPage.tsx`'s nine hero/feature sections each
// hand-roll a DIFFERENT hand-tuned decimal `fr` split (`0.88fr_1.12fr`,
// `0.72fr_1.28fr`, `0.75fr_1.25fr`, `0.82fr_1.18fr`, `1.2fr_0.8fr`,
// `0.7fr_1.3fr` — six distinct ratios across the nine sites, some reused).
// These are real, named, PROPORTIONAL ratios — same `fr`-unit discipline as
// `equal`/`2:1`/etc. above, just six more literal values because six more
// real call sites use them (same "closed subset of values real call sites
// already use" discipline `LayoutGap`'s own header comment states for the
// gap scale) — not a reopened arbitrary-value escape hatch. `align` (new,
// below) covers the one axis these hero sections vary on beyond ratio:
// `lg:items-center` / `lg:items-start` / `lg:items-end` cross-alignment,
// gated at `lg:` (meaningless below it, single stacked column) the same way
// the ratio classes themselves already are. Default (`align` unset) emits
// no `items-*` class — CSS grid's own default (`stretch`), matching every
// pre-existing non-hero `Split` consumer's own unstated behavior exactly.
// ------------------------------------------------------------------
export type SplitRatio =
  | 'equal'
  | '2:1'
  | '1:2'
  | '3:1'
  | '1:3'
  /** Fixed-width secondary pane, `1fr var(--panel-w)` — `ReportDetailPage.tsx`'s rail split. */
  | 'panel'
  | '0.88:1.12'
  | '0.72:1.28'
  | '0.75:1.25'
  | '0.82:1.18'
  | '1.2:0.8'
  | '0.7:1.3';

const SPLIT_RATIO_CLASS: Record<SplitRatio, string> = {
  equal: 'lg:grid-cols-split-equal',
  '2:1': 'lg:grid-cols-split-2-1',
  '1:2': 'lg:grid-cols-split-1-2',
  '3:1': 'lg:grid-cols-split-3-1',
  '1:3': 'lg:grid-cols-split-1-3',
  panel: 'lg:grid-cols-split-panel',
  '0.88:1.12': 'lg:grid-cols-split-88-112',
  '0.72:1.28': 'lg:grid-cols-split-72-128',
  '0.75:1.25': 'lg:grid-cols-split-75-125',
  '0.82:1.18': 'lg:grid-cols-split-82-118',
  '1.2:0.8': 'lg:grid-cols-split-120-80',
  '0.7:1.3': 'lg:grid-cols-split-70-130',
};

// Cross-axis alignment, `lg:`-gated (below `lg`, Split is one stacked
// column — cross-align is meaningless there, same reasoning `ratio` itself
// already carries). Reuses `CrossAlign` for the same closed vocabulary
// `Stack`/`Cluster`/`Grid` already share; `undefined` (the default) emits no
// class, i.e. CSS grid's own native `stretch` default, unchanged from
// every pre-2-147xd `Split` consumer's behavior.
const SPLIT_ALIGN_CLASS: Record<CrossAlign, string> = {
  start: 'lg:items-start',
  center: 'lg:items-center',
  end: 'lg:items-end',
  stretch: 'lg:items-stretch',
  baseline: 'lg:items-baseline',
};

export interface SplitProps {
  /** First pane — DOM/reading order first, per `AppFrame.tsx`'s own "DOM order is reading order" rule (no CSS-order trick reshuffles focus). */
  primary: ReactNode;
  secondary: ReactNode;
  /** Defaults to `'equal'` (1fr 1fr) — `ReviewDetailPage.tsx`'s own `lg:grid-cols-2` shape. */
  ratio?: SplitRatio;
  /** Defaults to `6` (1.5rem) — `OrgAccessPage.tsx`'s own two real split-grid gaps. */
  gap?: LayoutGap;
  /** Cross-axis alignment at `lg:` and up, story 2-147xd — the marketing hero sections' own `lg:items-center`/`lg:items-start`/`lg:items-end` variance. Omit for CSS grid's native `stretch` default (every non-hero consumer's existing, unstated behavior). */
  align?: CrossAlign;
  className?: string;
  'data-testid'?: string;
}

export function Split({
  primary,
  secondary,
  ratio = 'equal',
  gap = 6,
  align,
  className = '',
  'data-testid': testId,
}: SplitProps): JSX.Element {
  const alignClass = align ? SPLIT_ALIGN_CLASS[align] : '';
  return (
    <div
      className={`grid grid-cols-1 ${GAP_CLASS[gap]} ${SPLIT_RATIO_CLASS[ratio]} ${alignClass} ${className}`.trim()}
      data-testid={testId}
    >
      <div>{primary}</div>
      <div>{secondary}</div>
    </div>
  );
}

// ------------------------------------------------------------------
// Section — a semantic content section: optional heading block (title +
// description + trailing actions) plus vertically-rhythmed content. Formal-
// izes P10 §2.6's own "Section header" kind (#5: "bare `<h2>`, no
// card/slot contract of its own; `text-lg font-semibold text-foreground` is
// the convention every real consumer already follows — `BillingPage`,
// `DashboardPage`, `PlatformConsolePage`, `PricingPage`,
// `ActivationChecklistCard`") verbatim — same heading classes, same "no
// card/slot contract" scoping.
//
// Deliberately does NOT apply `bg-card`/`border`/`rounded-hairline` surface
// styling — that is P10 §2.7 row 5's own separately-named gap ("Flat surface
// / card wrapper — Named but not yet built ... Built by `2-147ta`"), a
// visual-treatment decision this story's own scope excludes ("pure LAYOUT
// primitives — no visual styling opinions beyond spacing/geometry"). A
// caller wanting the card-wrapped shape (`DashboardPage.tsx`'s/
// `PlatformConsolePage.tsx`'s own `rounded-hairline border border-border
// bg-card ... p-6` sections) still supplies that wrapper itself today, or
// composes `Section` inside `2-147ta`'s `Surface`/`Card` once it exists —
// not conflated here.
// ------------------------------------------------------------------
export interface SectionProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Trailing header-row actions, e.g. a "View my day" button (`DashboardPage.tsx`'s own "Next up" section). */
  actions?: ReactNode;
  /** Vertical rhythm between the heading block and `children`, and between children themselves. Defaults to `4` — `DashboardPage.tsx`'s own card-interior rhythm. */
  gap?: LayoutGap;
  as?: Tag;
  className?: string;
  'data-testid'?: string;
  children?: ReactNode;
}

export function Section({
  title,
  description,
  actions,
  gap = 4,
  as = 'section',
  className = '',
  'data-testid': testId,
  children,
}: SectionProps): JSX.Element {
  const Component = as;
  const hasHeader = title != null || description != null || actions != null;
  return (
    <Component className={`${SPACE_Y_CLASS[gap]} ${className}`} data-testid={testId}>
      {hasHeader && (
        <Cluster justify="between" align="start" gap={4}>
          <div>
            {title != null && <h2 className="text-lg font-semibold text-foreground">{title}</h2>}
            {description != null && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
          {actions != null && <Cluster gap={2}>{actions}</Cluster>}
        </Cluster>
      )}
      {children}
    </Component>
  );
}
